import React, { useEffect, useRef, useState } from 'react';
import { SIGNALCUE_ENV, APP_VERSION, APP_COMMIT } from '../config';

// Which environment am I looking at?
//
// Local and dev are reached at the SAME localhost ports: config.js's fallbacks
// are hardcoded to 8090/8092/8095, so scripts/dev_cloud_proxies.sh must use those
// exact ports for the dev bundle to reach anything. Two environments therefore
// sit behind identical URLs, and the failure is silent -- a dev frontend talking
// to a local database looks entirely normal, and a local frontend talking to dev
// looks entirely normal too. Distinct ports (this change's other half) make the
// URL informative; this makes the PAGE informative, which is what someone
// actually looks at.
//
// WHY SIGNALCUE_ENV AND NOT THE PORT. The port is a coincidence of how you
// happened to reach the page -- a proxy, a tunnel, a rebind all change it while
// the deployment stays the same. SIGNALCUE_ENV is what the deployment SAYS it
// is: docker-entrypoint.sh writes it into window._env_ from the container's own
// environment, which is also what nginx reads to decide X-Robots-Tag and what
// Userback already stamps on every bug report.
//
// NOTHING ON PRODUCTION. A badge on a customer's screen is noise, and the whole
// point is telling non-production environments apart from each other.

const TONE = {
  local: 'bg-slate-700',
  dev: 'bg-sky-700',
  staging: 'bg-amber-600',
};

const REPO_URL = 'https://github.com/SignalCue-ai/signalcue';

// Which build is this? The deploy sets APP_VERSION (a stg- tag, or the commit
// when no tag triggered the deploy) and APP_COMMIT. On dev they are the same
// commit, so it is shown once; on staging the line reads "tag · commit".
function versionLine() {
  if (!APP_VERSION && !APP_COMMIT) return '';
  if (!APP_COMMIT || APP_VERSION === APP_COMMIT) return APP_VERSION || APP_COMMIT;
  if (!APP_VERSION) return APP_COMMIT;
  return `${APP_VERSION} · ${APP_COMMIT}`;
}

export default function EnvironmentBanner() {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open]);

  if (SIGNALCUE_ENV === 'production') return null;

  // An empty or unrecognised value is shown rather than hidden. config.js
  // defaults to 'local', so empty means a deployment set it to nothing on
  // purpose or by accident -- and hiding the badge there would remove the
  // signal in exactly the case where the environment is least clear.
  const label = SIGNALCUE_ENV || 'unset';
  const version = versionLine();

  return (
    <div ref={rootRef} data-testid="environment-banner" className="fixed bottom-3 left-3 z-50">
      {open && (
        <div
          data-testid="environment-card"
          className="absolute bottom-full left-0 mb-2 w-64 rounded-lg border border-slate-200 bg-white p-3 text-xs text-slate-700 shadow-xl"
        >
          <div className="font-semibold uppercase tracking-wide text-slate-500">{label}</div>
          {version ? (
            <>
              <div className="mt-2 flex items-center justify-between gap-2">
                <span className="font-mono text-slate-900">{version}</span>
                <button
                  type="button"
                  className="rounded border border-slate-300 px-2 py-0.5 text-slate-600 hover:bg-slate-100"
                  onClick={() => navigator.clipboard && navigator.clipboard.writeText(version)}
                >
                  Copy
                </button>
              </div>
              {APP_COMMIT && (
                <a
                  className="mt-2 block text-sky-700 underline"
                  href={`${REPO_URL}/commit/${APP_COMMIT}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  View commit {APP_COMMIT} on GitHub
                </a>
              )}
            </>
          ) : (
            <div className="mt-2 text-slate-500">local build</div>
          )}
        </div>
      )}
      <button
        type="button"
        aria-label={`${label} environment — show version`}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className={`rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide text-white shadow-lg ${
          TONE[SIGNALCUE_ENV] || 'bg-rose-700'
        }`}
      >
        {label}
      </button>
    </div>
  );
}
